/* eslint-disable eqeqeq */
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { miniUuid, getShouldShowTapToPayForIOS, ErplyAttributes } from 'utils';
import { getGeneratedAttributes } from 'utils/payments';
import {
  setPaymentSelected,
  setPayment,
  setPaymentEditIndex,
  setPaymentDecimalEdit,
  updateCurrency,
} from 'actions/Payments';
import { PluginComponent } from 'plugins';
import {
  getCurrencyFormatterNoSymbol,
  getPosRoundCashFunction,
} from 'reducers/configs/settings';
import {
  getBalance,
  getIsPaymentDisabled,
  getCurrentSalesDocPaymentsCombo,
  getCurrentSalesDocument,
} from 'reducers/Payments';
import { getAllCurrencies } from 'reducers/configs/currency';
import { useBreakpoints } from 'utils/hooks/UI';

import styles from './PaymentList.module.scss';

const OriginalPaymentList = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation('payment');
  const salesDoc = useSelector(getCurrentSalesDocument) ?? {};
  const balance = useSelector(getBalance);
  const CURR = useSelector(getCurrencyFormatterNoSymbol);
  const originalPaymentsEnhanced = useSelector(getCurrentSalesDocPaymentsCombo);
  const checkPaymentIsDisabled = useSelector(getIsPaymentDisabled);
  const currencies = useSelector(getAllCurrencies);
  const roundCash = useSelector(getPosRoundCashFunction);
  const shouldShowTapToPayForIOS = getShouldShowTapToPayForIOS();
  const isMobileView = !useBreakpoints().mdplus;

  const handlePaymentItemClick = async payment => {
    (async () => {
      const amount = payment.remaining;
      if (balance <= 0) {
        return;
      }
      if (amount === 0 || payment.type === 'TIP') {
        return;
      }
      const currencyRate = Number(payment.currencyRate ?? 1);
      const newKey = `orig.${payment.paymentID}.${miniUuid()}`;
      const attributes = new ErplyAttributes(payment.attributes);

      const balanceInCurrency = balance / currencyRate;
      const paymentAmount =
        amount > balanceInCurrency ? balanceInCurrency : amount;
      if (currencyRate !== 1) {
        const currency = currencies.find(
          cur => cur.code === payment.currencyCode,
        );
        if (currency) {
          await dispatch(updateCurrency(currency));
        }
      }

      const withGeneratedAttributes = getGeneratedAttributes(payment);

      await dispatch(
        setPayment({
          type: payment.type,
          amount:
            payment.type === 'CASH' ? roundCash(paymentAmount) : paymentAmount,
          cardNumber: payment.cardNumber,
          caption: payment.caption ?? payment.type,
          paymentIntegration: payment.paymentIntegration,
          key: newKey,
          serial: payment.type === 'GIFTCARD' ? payment.cardNumber : undefined,
          giftCardBalance: payment.giftCardBalance,
          attributes: withGeneratedAttributes,
          original: {
            ...payment,
            attributes: attributes.asDict,
          },
          currencyCode: payment.currencyCode,
          currencyRate,
        }),
      );
      await dispatch(setPaymentEditIndex(0));
      await dispatch(setPaymentDecimalEdit(false));
      const keyToSelect = isMobileView ? '' : newKey;
      await dispatch(setPaymentSelected(keyToSelect));
    })();
  };

  return (
    <div className={styles['payment-list']} data-testid="original-payment-list">
      <div className="payment-total">
        <span className="payment-total-label" data-testid="payment-total">
          {t('originalPayments.total', {
            total: CURR.stringify(salesDoc.paid),
          })}
        </span>
      </div>
      <div className="payments" data-testid="payments">
        <ul className="payment-list-group">
          {originalPaymentsEnhanced
            ? originalPaymentsEnhanced.map(payment => {
                const paymentKey = payment.paymentID;
                // eslint-disable-next-line yoda
                const isUsed = 0 < payment.spent;
                const isUsedCompletely = payment.remaining <= 0;
                const attributes = new ErplyAttributes(payment.attributes);
                const isTipPayment =
                  payment.type === 'TIP' ||
                  Boolean(attributes.get('cardIsTip'));
                const wasMadeWithTapToPay = Boolean(
                  payment.attributes?.isTapToPayPayment,
                );
                const disabled =
                  Number(balance) <= 0 ||
                  isUsedCompletely ||
                  isTipPayment ||
                  (wasMadeWithTapToPay && !shouldShowTapToPayForIOS) ||
                  checkPaymentIsDisabled(payment.type);

                function getTitle() {
                  if (disabled) {
                    if (checkPaymentIsDisabled(payment.type)) {
                      return t('alerts.paymentDisabled');
                    }
                    if (isUsedCompletely) {
                      return t('alerts.usedCompletely');
                    }
                    if (isTipPayment) {
                      return t('alerts.tipPayment');
                    }
                    return t('alerts.amountCovered');
                  }
                  return undefined;
                }
                const title = getTitle();

                const getBorderColor = () => {
                  if (isUsedCompletely || isTipPayment) {
                    return 'maroon';
                  }
                  if (isUsed) {
                    return 'orange';
                  }
                  return undefined;
                };
                const borderColor = getBorderColor();
                return (
                  <li
                    key={paymentKey}
                    className="payment-item"
                    data-testid="original-payment-item"
                    data-test-type={payment.type}
                    data-test-partiallyused={isUsed}
                    data-test-completelyused={isUsedCompletely}
                    data-test-istippayment={isTipPayment}
                    data-test-serial={!!payment.serial} // Requested to be boolean by Daniel
                  >
                    <PluginComponent
                      hookname="UIOriginalPayment"
                      props={{
                        payment,
                        disabled,
                        onClick: () => handlePaymentItemClick(payment),
                      }}
                    >
                      <div
                        style={{
                          cursor: disabled ? 'not-allowed' : 'pointer',
                          borderColor,
                          opacity: isUsedCompletely || isTipPayment ? 0.7 : 1,
                        }}
                        tabIndex={0}
                        disabled={disabled}
                        title={title}
                        data-testid="payment-item-container"
                        onClick={
                          disabled
                            ? undefined
                            : () => handlePaymentItemClick(payment)
                        }
                        className="button"
                        role="button"
                      >
                        <div className="type">
                          <span data-testid="payment-type">{payment.type}</span>
                          <PluginComponent hookname="UIOriginalPaymentCurrency">
                            <span className="small text-muted">
                              {payment.currencyCode}
                            </span>
                          </PluginComponent>

                          {payment.cardNumber ? (
                            <span data-testid="payment-card-number">
                              {payment.cardNumber}
                            </span>
                          ) : null}
                        </div>
                        <span data-testid="payment-amount" className="amount">
                          {CURR.stringify(payment.sum)}
                        </span>
                      </div>
                    </PluginComponent>
                  </li>
                );
              })
            : null}
        </ul>
      </div>
      <div className="payment-balance" data-testid="payment-change">
        <span className="payment-balance-value pull-right" />
      </div>
    </div>
  );
};

export default OriginalPaymentList;
